import {
    HStack,
    VStack,
    Box,
    Button,
    IconButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { useQueueStore } from '../../queues/context/use-queue-store';
import { HospitalQueueStore } from '../store/hospital-queue-store';
import { QueueFilterMultiselect } from '../../common/components/queues-filter-select';
import { useHospitalQueueAttributeFilter } from '../hooks/use-hospital-queue-attribute-filter';

export const HospitalAttributeFilterPopover = observer(
    function HospitalAttributeFilterPopover(): React.ReactElement {
        const queueStore = useQueueStore() as HospitalQueueStore;
        const {
            hospitalCapabilityFilters,
            hospitalStatusFilters,
        } = queueStore.workspacePanel.queue;

        const {
            options: hospitalCapabilityOptions,
            selectedFilterValues: selectedCapabilityFilterValues,
        } = useHospitalQueueAttributeFilter('HOSPITAL_CAPABILITY', hospitalCapabilityFilters);

        const {
            options: hospitalStatusOptions,
            selectedFilterValues: selectedStatusFilterValues,
        } = useHospitalQueueAttributeFilter('HOSPITAL_STATUS', hospitalStatusFilters);

        const totalNumberOfFiltersSelected = useMemo(
            () => selectedCapabilityFilterValues.length + selectedStatusFilterValues.length,
            [selectedCapabilityFilterValues, selectedStatusFilterValues]
        );

        const onChangeStatuses = useCallback(
            (attrIds: number[]) => {
                queueStore.setHospitalStatusAttributeIds(attrIds);
            },
            [queueStore]
        );

        const onChangeCapabilities = useCallback(
            (attrIds: number[]) => {
                queueStore.setHospitalCapabilityAttributeIds(attrIds);
            },
            [queueStore]
        );

        const showClearAll = useMemo(() => totalNumberOfFiltersSelected > 0, [
            totalNumberOfFiltersSelected,
        ]);

        const showSelectAll = useMemo(
            () =>
                totalNumberOfFiltersSelected <
                hospitalCapabilityOptions.length + hospitalStatusOptions.length,
            [
                totalNumberOfFiltersSelected,
                hospitalCapabilityOptions.length,
                hospitalStatusOptions.length,
            ]
        );

        const clearAll = useCallback(() => {
            onChangeStatuses([]);
            onChangeCapabilities([]);
        }, [onChangeCapabilities, onChangeStatuses]);

        const selectAll = useCallback(() => {
            onChangeStatuses(hospitalStatusOptions.map((option) => option.value) as number[]);
            onChangeCapabilities(
                hospitalCapabilityOptions.map((option) => option.value) as number[]
            );
        }, [
            hospitalCapabilityOptions,
            hospitalStatusOptions,
            onChangeCapabilities,
            onChangeStatuses,
        ]);

        const isActive = !!hospitalCapabilityFilters.length || !!hospitalStatusFilters.length;

        return (
            <Popover hasArrow={false}>
                <PopoverTrigger asChild>
                    <IconButton
                        aria-label="filter by hospital attributes"
                        icon="Filter"
                        isActive={isActive}
                        variant="ghost"
                        data-testid="hospital-attribute-filter-popover-button"
                    />
                </PopoverTrigger>
                <PopoverContent align="start">
                    <VStack data-testid="hospital-attribute-filter-popover-body">
                        <HStack justifyContent="flex-end" alignItems="center" w="100%">
                            {showClearAll && (
                                <Button variant="ghost" onClick={clearAll}>
                                    Clear All
                                </Button>
                            )}
                            {showSelectAll && (
                                <Button variant="ghost" onClick={selectAll}>
                                    Select All
                                </Button>
                            )}
                        </HStack>
                        <Box
                            pr={0}
                            minWidth="350px"
                            maxWidth="350px"
                            maxHeight="500px"
                            overflowY="auto"
                            textTransform="none"
                        >
                            <QueueFilterMultiselect
                                label="Hospital Status"
                                attributeOptions={hospitalStatusOptions}
                                values={selectedStatusFilterValues}
                                onChange={(values) => onChangeStatuses(values as number[])}
                            />

                            <QueueFilterMultiselect
                                label="Hospital Capability"
                                attributeOptions={hospitalCapabilityOptions}
                                values={selectedCapabilityFilterValues}
                                onChange={(values) => onChangeCapabilities(values as number[])}
                            />
                        </Box>
                    </VStack>
                </PopoverContent>
            </Popover>
        );
    }
);
